import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { formatearLlaveRepuesto } from '@actual-app/core/shared/llave-repuesto';

import { Boton } from '#components/mobile/ui/Boton';
import { Icono } from '#components/mobile/ui/Icono';
import { color, radio, texto } from '#components/mobile/ui/tokens';

import { crearPdf } from './pdfLlave';

type LlaveRepuestoProps = {
  llave: string;
  onContinuar: () => void;
};

/**
 * Enseña la llave de repuesto una sola vez: copiarla (para pegarla luego) o
 * guardarla en PDF, y confirmar que se ha guardado antes de seguir.
 */
export function LlaveRepuesto({ llave, onContinuar }: LlaveRepuestoProps) {
  const { t } = useTranslation();
  const [copiada, setCopiada] = useState(false);
  const [guardada, setGuardada] = useState(false);
  const formateada = formatearLlaveRepuesto(llave);

  async function copiar() {
    try {
      await navigator.clipboard.writeText(formateada);
      setCopiada(true);
    } catch {
      setCopiada(false);
    }
  }

  function guardarPdf() {
    const fecha = new Date().toLocaleDateString();
    const pdf = crearPdf([
      { texto: t('Rumbo'), tamano: 12, negrita: true },
      { texto: '', tamano: 12 },
      { texto: t('Your spare key'), tamano: 22, negrita: true },
      { texto: '', tamano: 8 },
      { texto: formateada, tamano: 20, mono: true },
      { texto: '', tamano: 8 },
      {
        texto: t(
          'If you forget your Rumbo password, this key opens your data. Nobody else has it, not even Rumbo.',
        ),
        tamano: 12,
      },
      {
        texto: t(
          'Keep it somewhere safe and private: whoever has it can open your data.',
        ),
        tamano: 12,
      },
      { texto: '', tamano: 8 },
      { texto: t('Created on {{fecha}}', { fecha }), tamano: 10 },
    ]);
    const url = URL.createObjectURL(
      new Blob([pdf as BlobPart], { type: 'application/pdf' }),
    );
    const enlace = document.createElement('a');
    enlace.href = url;
    enlace.download = 'rumbo-llave-de-repuesto.pdf';
    enlace.click();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  }

  return (
    <View style={{ gap: 18 }}>
      <View
        style={{
          padding: 18,
          backgroundColor: color.surface,
          border: `1.5px solid ${color.line}`,
          borderRadius: radio.tarjeta,
          alignItems: 'center',
        }}
      >
        <Text
          aria-label={t('Spare key')}
          style={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 21,
            fontWeight: 700,
            letterSpacing: '0.06em',
            lineHeight: 1.5,
            textAlign: 'center',
            color: color.fg,
            userSelect: 'all',
          }}
        >
          {/* Dos filas de tres grupos: se lee y se copia a mano mejor. */}
          {formateada.slice(0, 14)}
          <br />
          {formateada.slice(15)}
        </Text>
      </View>

      <View style={{ flexDirection: 'row', gap: 10 }}>
        <Boton
          variante="fantasma"
          onPress={() => void copiar()}
          style={{ flex: 1 }}
        >
          <Icono nombre={copiada ? 'check' : 'copy'} size={18} />
          {copiada ? t('Key copied') : t('Copy')}
        </Boton>
        <Boton variante="fantasma" onPress={guardarPdf} style={{ flex: 1 }}>
          <Icono nombre="download" size={18} />
          {t('Save PDF')}
        </Boton>
      </View>

      <label
        style={{
          ...texto.cuerpo,
          display: 'flex',
          gap: 12,
          alignItems: 'flex-start',
          color: color.fg,
          minHeight: 44,
          cursor: 'pointer',
        }}
      >
        <input
          type="checkbox"
          checked={guardada}
          onChange={e => setGuardada(e.target.checked)}
          style={{ width: 22, height: 22, margin: 0, flexShrink: 0 }}
        />
        {t("I've saved it somewhere safe (PDF, paper or password manager)")}
      </label>

      <Boton bloque isDisabled={!guardada} onPress={onContinuar}>
        <Trans>Continue</Trans>
      </Boton>
    </View>
  );
}
